import React, { useMemo, useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import { useUsers } from '../context/UsersContext';
import { C, S } from '../utils/styles';
import { fmt } from '../utils/formatters';
import { calculateScenario } from '../utils/calculations';
import CalculationAudit from './CalculationAudit';
import Icon from './Icon';

function getFilingStatusLabel(filing) {
  const labels = {
    single: 'Single (S)',
    married: 'Married Filing Jointly (MFJ)',
    mfs: 'Married Filing Separately (MFS)',
    hoh: 'Head of Household (HoH)',
    qss: 'Qualifying Surviving Spouse (QSS)'
  };
  return labels[filing] || filing;
}

export default function SpreadView({ salary, state, filing }) {
  const { config } = useConfig();
  const { currentUser } = useUsers();
  const [isAllocationExpanded, setIsAllocationExpanded] = useState(false);

  const analysis = useMemo(() => {
    // Filter active plans and sort by premium (descending - highest at top)
    const activePlans = (config.planTiers || [])
      .filter(tier => !tier.archived)
      .sort((a, b) => b.monthlyPremium - a.monthlyPremium);
    
    return activePlans.map((tier) => {
      // SpreadView always uses Monthly display (periods = 12)
      const calc = calculateScenario(salary, state, filing, tier.monthlyPremium, tier.monthlyClaim, config, 12);
      
      // Calculate viability
      const annualPremium = tier.monthlyPremium * 12;
      const residual = salary - annualPremium;
      const annualHours = config.engineSettings?.annualHoursForMinWage || 2080;
      const hourly = residual / annualHours;
      const minWage = config.payrollConstants?.minWageHourly || 7.25;
      const wageOk = hourly >= minWage && residual > 0;
      const netPositive = calc.diff.netTakeHome >= 0;
      const viable = wageOk && netPositive;
      
      return {
        ...tier,
        taxSavings: calc.diff.totalTax,
        empNet: calc.diff.netTakeHome,
        emprFica: calc.employerFicaSavingsPerPeriod,
        netCostToEmp: tier.spread - calc.diff.totalTax,
        viable,
        annualNet: calc.diff.netTakeHome * 12
      };
    });
  }, [salary, state, filing, config]);

  // Find the optimal viable plan for the audit
  const optimalPlan = useMemo(() => {
    const viablePlans = analysis.filter(p => p.viable);
    if (viablePlans.length === 0) return analysis[0]; // If no viable plans, use highest
    // Return the viable plan with highest annual net benefit
    return viablePlans.sort((a, b) => b.annualNet - a.annualNet)[0];
  }, [analysis]);

  return (
    <div data-testid="spread-view" style={S.card}>
      <h3 style={S.heading}>Plan Economics Analysis — {fmt(salary)} / {config.stateData[state]?.name} / {getFilingStatusLabel(filing)}</h3>
      <p style={{ fontSize: 12, color: C.textLight, margin: "0 0 14px" }}>
        How the spread works: Premium − Claim = Spread (HC Revenue). Tax savings offset the spread for the employee.
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={S.th}>Plan</th>
            <th style={{ ...S.th, textAlign: "right" }}>Premium</th>
            <th style={{ ...S.th, textAlign: "right" }}>Claim</th>
            <th style={{ ...S.th, textAlign: "right" }}>Spread (\u2192 HC)</th>
            <th style={{ ...S.th, textAlign: "right" }}>Tax Savings</th>
            <th style={{ ...S.th, textAlign: "right" }}>Emp Net/Mo</th>
            <th style={{ ...S.th, textAlign: "right" }}>Emp Net/Yr</th>
            <th style={{ ...S.th, textAlign: "right" }}>Empr FICA/Mo</th>
            <th style={{ ...S.th, textAlign: "right" }}>Empr FICA/Yr</th>
            <th style={{ ...S.th, textAlign: "right" }}>HC Rev/Yr</th>
          </tr>
        </thead>
        <tbody>
          {analysis.map((r) => (
            <tr key={r.id}>
              <td style={{ ...S.td, fontWeight: 700, color: C.text }}>{r.label}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11 }}>{fmt(r.monthlyPremium)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11 }}>{fmt(r.monthlyClaim)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: C.purple, fontWeight: 700 }}>{fmt(r.spread)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: C.green, fontWeight: 600 }}>{fmt(r.taxSavings)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, fontWeight: 700, color: r.empNet >= 0 ? C.green : C.red }}>{r.empNet >= 0 ? "+" : ""}{fmt(r.empNet)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, fontWeight: 700, color: r.empNet >= 0 ? C.green : C.red }}>{r.empNet >= 0 ? "+" : ""}{fmt(r.empNet * 12)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: C.blue }}>{fmt(r.emprFica)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: C.blue, fontWeight: 700 }}>{fmt(r.emprFica * 12)}</td>
              <td style={{ ...S.td, textAlign: "right", ...S.mono, fontSize: 11, color: C.purple, fontWeight: 700 }}>{fmt(r.spread * 12)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Revenue Allocation Breakdown - Collapsible */}
      {config.revenueSettings?.showAllocationInReports && config.revenueSettings?.spreadAllocation && (
        <div style={{ marginTop: 20, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          {/* Collapsible Header */}
          <button
            onClick={() => setIsAllocationExpanded(!isAllocationExpanded)}
            style={{
              width: '100%',
              padding: '14px 16px',
              background: C.bg,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = C.accentBg}
            onMouseLeave={(e) => e.currentTarget.style.background = C.bg}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icon 
                name="pie-chart-outline" 
                style={{ fontSize: 16, color: C.purple }} 
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                Spread Allocation Breakdown
              </span>
            </div>
            <Icon 
              name={isAllocationExpanded ? 'chevron-up-outline' : 'chevron-down-outline'} 
              style={{ fontSize: 18, color: C.textMid }} 
            />
          </button>
          
          {/* Collapsible Content */}
          {isAllocationExpanded && (
            <div style={{ padding: 16, background: 'white', borderTop: `1px solid ${C.border}` }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, textAlign: 'left' }}>Category</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>% of Spread</th>
                    {analysis.slice(0, 3).map(plan => (
                      <th key={plan.id} style={{ ...S.th, textAlign: 'right' }}>{plan.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {config.revenueSettings.spreadAllocation.map((allocation, idx) => (
                    <tr key={idx}>
                      <td style={{ ...S.td, fontSize: 12, color: C.text }}>{allocation.category}</td>
                      <td style={{ ...S.td, textAlign: 'right', ...S.mono, fontSize: 11 }}>{allocation.percentage}%</td>
                      {analysis.slice(0, 3).map(plan => {
                        const amount = (plan.spread * allocation.percentage) / 100;
                        return (
                          <td key={plan.id} style={{ ...S.td, textAlign: 'right', ...S.mono, fontSize: 11, color: C.textMid }}>
                            {fmt(amount)}/mo
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr style={{ borderTop: `2px solid ${C.border}`, fontWeight: 700 }}>
                    <td style={{ ...S.td, fontSize: 12, color: C.text }}>Total</td>
                    <td style={{ ...S.td, textAlign: 'right', ...S.mono, fontSize: 11 }}>
                      {config.revenueSettings.spreadAllocation.reduce((sum, a) => sum + a.percentage, 0)}%
                    </td>
                    {analysis.slice(0, 3).map(plan => (
                      <td key={plan.id} style={{ ...S.td, textAlign: 'right', ...S.mono, fontSize: 11, color: C.purple, fontWeight: 700 }}>
                        {fmt(plan.spread)}/mo
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: 12, fontSize: 10, color: C.textLight, fontStyle: 'italic' }}>
                Shows first 3 plans only. Allocation percentages apply to all tiers.
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Calculation Audit for Super Users, Simple Explanation for Others */}
      {currentUser?.role === 'super_user' ? (
        // Super Users: Show detailed calculation audit
        analysis.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <CalculationAudit
              salary={salary}
              state={state}
              filing={filing}
              selectedPlan={optimalPlan}
              config={config}
              currentUser={currentUser}
            />
          </div>
        )
      ) : (
        // Non-Super Users: Show simple explanation
        <div style={{ marginTop: 14, padding: "12px 16px", background: C.greenLight, borderRadius: 10, border: `1px solid ${C.greenBorder}`, fontSize: 12, color: C.green, lineHeight: 1.6 }}>
          <strong>How it works:</strong> The employee's tax savings (shown in green) more than offset the spread (HC revenue), resulting in a net take-home increase. The employer saves pure FICA on the full premium amount at zero cost. HealthCues earns the spread to cover vendor costs, hospitalization underwriting, broker commissions, and margin.
        </div>
      )}
    </div>
  );
}
